import { Button } from "../atoms/button.js";
import { cx } from "../internal/cx.js";

export interface RouteErrorProps {
  readonly error: Error;
  readonly onReset?: () => void;
  readonly homeHref?: string;
  /** Optional request-id (for support copy-paste). */
  readonly requestId?: string;
  readonly className?: string;
}

/**
 * Page-level error fallback rendered by `<ErrorBoundary>` when a render
 * error escapes a route. Always shows a friendly heading + "Try again"
 * + "Back to home". The `requestId` is shown when present so the user
 * can quote it to support.
 */
export function RouteError({
  error,
  onReset,
  homeHref = "/",
  requestId,
  className,
}: RouteErrorProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cx(
        "mx-auto my-12 max-w-md rounded-2xl border border-[var(--cw-v2-color-border)] bg-[var(--cw-v2-color-surface)] p-8 text-center shadow-sm",
        className,
      )}
    >
      <h1 className="text-xl font-semibold text-[var(--cw-v2-color-text)]">Something went wrong</h1>
      <p className="mt-3 text-sm text-[var(--cw-v2-color-text-muted)]">
        We couldn't load this page. Please try again or head back to the start of your sign-up.
      </p>
      <p className="mt-4 break-all text-xs text-[var(--cw-v2-color-text-muted)] opacity-70">
        {error.message}
      </p>
      {requestId ? (
        <p className="mt-1 text-xs text-[var(--cw-v2-color-text-muted)] opacity-70">
          Reference: <code className="font-mono">{requestId}</code>
        </p>
      ) : null}
      <div className="mt-6 flex justify-center gap-3">
        {onReset ? (
          <Button variant="primary" size="md" onClick={onReset}>
            Try again
          </Button>
        ) : null}
        <Button variant="secondary" size="md" asChild>
          <a href={homeHref}>Back to home</a>
        </Button>
      </div>
    </div>
  );
}
